import { PLOTLY_THEME_DEFAULTS, PLOTLY_THEME_DEFAULTS_DARK, AXIS_LAYOUT_KEY_PATTERN } from "./constants"

export type ColorScheme = "light" | "dark"

type PlainObject = Record<string, unknown>

function isPlainObject(value: unknown): value is PlainObject {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function deepMerge(base: unknown, overrides: unknown): unknown {
  if (!isPlainObject(base)) {
    return overrides
  }

  if (!isPlainObject(overrides)) {
    return base
  }

  const merged: PlainObject = { ...base }

  Object.keys(overrides).forEach((key) => {
    const overrideValue = overrides[key]
    const baseValue = merged[key]
    merged[key] = isPlainObject(baseValue) && isPlainObject(overrideValue)
      ? deepMerge(baseValue, overrideValue)
      : overrideValue
  })

  return merged
}

function normalizePlotlyTitle(title: unknown): unknown {
  if (typeof title === "string") {
    return { text: title }
  }

  return title
}

function normalizePlotlyLayout(layout: unknown): PlainObject {
  const sourceLayout = isPlainObject(layout) ? layout : {}

  const normalizedLayout: PlainObject = { ...sourceLayout }
  if (sourceLayout.title !== undefined) {
    normalizedLayout.title = normalizePlotlyTitle(sourceLayout.title)
  }

  Object.keys(sourceLayout).forEach((key) => {
    const axisLayout = sourceLayout[key]
    if (!AXIS_LAYOUT_KEY_PATTERN.test(key) || !isPlainObject(axisLayout)) {
      return
    }

    const normalizedAxis: PlainObject = { ...axisLayout }
    if (axisLayout.title !== undefined) {
      normalizedAxis.title = normalizePlotlyTitle(axisLayout.title)
    }
    normalizedLayout[key] = normalizedAxis
  })

  return normalizedLayout
}

function createAxisTheme(textColor: string, secondaryTextColor: string, dividerColor: string): PlainObject {
  return {
    tickfont: { color: textColor },
    title: { font: { color: textColor } },
    gridcolor: dividerColor,
    zerolinecolor: secondaryTextColor,
    linecolor: dividerColor,
  }
}

export interface PlotlyLayoutColors {
  textColor?: string
  secondaryTextColor?: string
  dividerColor?: string
  paperColor?: string
}

interface SchemePalette {
  text?: { primary?: string, secondary?: string }
  divider?: string
  background?: { paper?: string }
}

interface ThemeWithColorSchemes {
  colorSchemes?: Partial<Record<string, { palette?: SchemePalette } | undefined>>
}

// The active scheme's RESOLVED palette (theme.colorSchemes[scheme].palette holds real color strings in CSS-vars
// mode — theme.vars.palette.* are the var refs, theme.palette.* is frozen to the default scheme). Reading it is
// synchronous in the same render as a useColorScheme toggle, so the re-plot never races a DOM CSS-var recompute.
function resolveThemeColors(theme: ThemeWithColorSchemes | null | undefined, scheme: ColorScheme): PlotlyLayoutColors | null {
  const palette = theme?.colorSchemes?.[scheme]?.palette
  if (!palette) return null
  return {
    textColor: palette.text?.primary,
    secondaryTextColor: palette.text?.secondary,
    dividerColor: palette.divider,
    paperColor: palette.background?.paper,
  }
}

// Chart chrome for the active scheme. `colors` are the resolved theme palette; `scheme` picks the no-palette
// fallback (tests / no provider).
function createThemeLayout(
  colors?: PlotlyLayoutColors | null,
  scheme?: ColorScheme | null,
): PlainObject {
  const fallback = scheme === "dark" ? PLOTLY_THEME_DEFAULTS_DARK : PLOTLY_THEME_DEFAULTS

  const textColor = colors?.textColor ?? fallback.textColor
  const secondaryTextColor = colors?.secondaryTextColor ?? fallback.textSecondaryColor
  const dividerColor = colors?.dividerColor ?? fallback.dividerColor
  const paperColor = colors?.paperColor ?? fallback.bgColor

  return {
    paper_bgcolor: paperColor,
    plot_bgcolor: paperColor,
    font: { color: textColor },
    // Placement is Plotly's own — the theme only colours the title. Overriding it is what caused the overlap this
    // fixes: `automargin` parks the title above the container edge, and the `yref: "paper"` added to undo that
    // clipping dropped it onto make_subplots' subplot titles. `resolveTopMargin` sizes the band instead.
    title: { font: { color: textColor } },
    xaxis: createAxisTheme(textColor, secondaryTextColor, dividerColor),
    yaxis: createAxisTheme(textColor, secondaryTextColor, dividerColor),
    // Pin the legend to the right of the plot (vertical). The docked panel is height-constrained but not
    // width-constrained, so a top/bottom legend fights the title/x-axis for scarce vertical room; the right
    // uses horizontal space and never overlaps the title at any height. Outside placement auto-expands the margin.
    legend: {
      bgcolor: paperColor,
      bordercolor: dividerColor,
      font: { color: textColor },
      orientation: "v",
      xanchor: "left",
      x: 1.02,
      yanchor: "top",
      y: 1,
    },
    modebar: {
      bgcolor: paperColor,
      color: secondaryTextColor,
      activecolor: textColor,
    },
    hoverlabel: {
      bgcolor: paperColor,
      bordercolor: dividerColor,
      font: { color: textColor },
    },
  }
}

// make_subplots emits subplot titles as paper-anchored annotations sitting on the plot-area top edge — the BOTTOM
// of the same top margin Plotly centres the figure title in. Annotations have no `automargin` (they are absent from
// Plotly's schema), so nothing in the library reserves their band; this is the one part it can't do for us.
function isSubplotTitleAnnotation(annotation: unknown): annotation is PlainObject {
  if (!isPlainObject(annotation)) return false
  return annotation.yref === "paper" && typeof annotation.y === "number" && annotation.y >= 1 && annotation.yanchor !== "top"
}

function textLineCount(text: unknown): number {
  return String(text ?? "").split(/<br\s*\/?>/i).length
}

function textFontSize(block: PlainObject, fallbackFontSize: number): number {
  const font = isPlainObject(block.font) ? block.font : null
  return typeof font?.size === "number" ? font.size : fallbackFontSize
}

// A block that sets no size of its own inherits `layout.font` — annotations at 1:1, the figure title at 1.4× —
// so the reservation has to follow the figure's base size instead of assuming Plotly's 12px default.
function layoutFontSize(layout: PlainObject): number {
  const font = isPlainObject(layout.font) ? layout.font : null
  return typeof font?.size === "number" ? font.size : PLOTLY_THEME_DEFAULTS.baseFontSize
}

// How far the title reaches below its own anchor: Plotly puts the FIRST line's baseline at the middle of the top
// margin and flows the rest downwards, so only the extra lines and the descender count.
function titleDropBelowCentre(title: PlainObject, fallbackFontSize: number): number {
  const fontSize = textFontSize(title, fallbackFontSize)
  const extraLines = textLineCount(title.text) - 1
  return extraLines * fontSize * PLOTLY_THEME_DEFAULTS.textLineHeightRatio + fontSize * PLOTLY_THEME_DEFAULTS.titleDescenderRatio
}

// Top margin big enough for both blocks that live in it. The subplot-title band hangs from the margin's bottom
// edge; a figure title, when there is one, is CENTRED in the margin, so everything below its anchor has to fit in
// the lower half — which is why only that case doubles.
function resolveTopMargin(baseTopMargin: number, layout: unknown): number {
  const sourceLayout = isPlainObject(layout) ? layout : {}
  const annotations = Array.isArray(sourceLayout.annotations) ? sourceLayout.annotations : []
  const baseFontSize = layoutFontSize(sourceLayout)
  const annotationBand = annotations
    .filter(isSubplotTitleAnnotation)
    .map((annotation) => textLineCount(annotation.text) * textFontSize(annotation, baseFontSize) * PLOTLY_THEME_DEFAULTS.textLineHeightRatio)
    .reduce((tallest, height) => Math.max(tallest, height), 0)

  const title = isPlainObject(sourceLayout.title) ? sourceLayout.title : null
  const band = annotationBand + PLOTLY_THEME_DEFAULTS.topMarginGap
  const titleFallbackFontSize = baseFontSize * PLOTLY_THEME_DEFAULTS.titleFontScale
  const needed = title?.text ? 2 * (titleDropBelowCentre(title, titleFallbackFontSize) + band) : band
  return Math.max(baseTopMargin, Math.ceil(needed))
}

const PRIMARY_AXIS_KEYS = new Set(["xaxis", "yaxis"])

function applyAxisThemeToLayout(layout: unknown, axisTheme: unknown): unknown {
  if (!isPlainObject(layout)) {
    return layout
  }

  const theme = isPlainObject(axisTheme) ? axisTheme : null
  if (!theme) {
    return layout
  }

  const nextLayout: PlainObject = { ...layout }

  Object.keys(layout).forEach((key) => {
    const axisLayout = layout[key]
    const isAxisKey = AXIS_LAYOUT_KEY_PATTERN.test(key) && isPlainObject(axisLayout)
    const isExtraAxis = isAxisKey && !PRIMARY_AXIS_KEYS.has(key)
    if (isExtraAxis) {
      // Themed chrome (gridcolor/tickfont/…) wins over the incoming axis; its data keys (range/type/…) survive.
      nextLayout[key] = deepMerge(axisLayout, theme)
    }
  })

  return nextLayout
}

export {
  applyAxisThemeToLayout,
  createThemeLayout,
  deepMerge,
  normalizePlotlyLayout,
  resolveThemeColors,
  resolveTopMargin,
}
